/// NodePropertyForm: Renders property form for Node editor elements.
/// Shows common fields (name, type, category), socket definitions with
/// dynamic add/remove controls, and group dissolve button.
/// Phase 15, Plan 11 -- gap closure: added dynamic socket +/- buttons and group controls.

import { useCallback } from "react";
import { PropertyField } from "./PropertyField";
import type { NodeElementProperties, NodeSocketType } from "@/types/properties";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface NodePropertyFormProps {
  properties: NodeElementProperties;
  onChange: (updated: Partial<NodeElementProperties>) => void;
}

// ---------------------------------------------------------------------------
// Socket type display colors
// ---------------------------------------------------------------------------

const SOCKET_TYPE_COLORS: Record<NodeSocketType, string> = {
  number: "text-blue-400",
  string: "text-green-400",
  boolean: "text-yellow-400",
  any: "text-zinc-300",
  flow: "text-purple-400",
  array: "text-cyan-400",
  object: "text-orange-400",
  trigger: "text-red-400",
};

// ---------------------------------------------------------------------------
// Helper: dispatch socket events
// ---------------------------------------------------------------------------

function dispatchAddSocket(
  groupId: number,
  socketId: string,
  name: string,
  dataType: string,
  isInput: boolean,
) {
  window.dispatchEvent(
    new CustomEvent("flowy:addSocket", {
      detail: { groupId, socketId, name, dataType, isInput },
    }),
  );
}

function dispatchRemoveSocket(groupId: number, socketId: string, isInput: boolean) {
  window.dispatchEvent(
    new CustomEvent("flowy:removeSocket", {
      detail: { groupId, socketId, isInput },
    }),
  );
}

function dispatchDissolveGroup(groupId: number) {
  window.dispatchEvent(new CustomEvent("flowy:dissolveGroup", { detail: { groupId } }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NodePropertyForm({ properties, onChange }: NodePropertyFormProps) {
  const groupId = parseInt(properties.id, 10);

  // Dynamic socket add handlers
  const handleAddInput = useCallback(() => {
    const count = properties.inputs.length;
    const socketId = `in_dyn_${count}`;
    const name = `Input ${count + 1}`;
    dispatchAddSocket(groupId, socketId, name, "any", true);
  }, [groupId, properties.inputs.length]);

  const handleAddOutput = useCallback(() => {
    const count = properties.outputs.length;
    const socketId = `out_dyn_${count}`;
    const name = `Output ${count + 1}`;
    dispatchAddSocket(groupId, socketId, name, "any", false);
  }, [groupId, properties.outputs.length]);

  // Check if this is a group node (nodeType contains "group" marker)
  const isGroup = properties.nodeType === "group" || properties.category === "group";

  return (
    <div className="flex flex-col gap-4 p-3">
      {/* General section */}
      <Section title="General">
        <PropertyField
          label="Name"
          type="text"
          value={properties.name}
          onChange={(v) => onChange({ name: String(v) })}
          placeholder="Node name"
        />
        <PropertyField
          label="ID"
          type="text"
          value={properties.id}
          onChange={() => undefined}
          placeholder="Node identifier"
          disabled
        />
        <PropertyField
          label="Type"
          type="text"
          value={properties.nodeType}
          onChange={() => undefined}
          disabled
        />
        <PropertyField
          label="Category"
          type="text"
          value={properties.category}
          onChange={() => undefined}
          disabled
        />
      </Section>

      {/* Group controls: show dissolve button for group containers */}
      {isGroup && (
        <Section title="Group">
          <button
            type="button"
            className="w-full rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500 transition-colors"
            onClick={() => dispatchDissolveGroup(groupId)}
          >
            Dissolve Group
          </button>
          <p className="text-xs text-zinc-500">
            Ungroups the contained nodes back to individual elements.
          </p>
        </Section>
      )}

      {/* Inputs section with dynamic add/remove */}
      <Section title={`Inputs (${properties.inputs.length})`}>
        {properties.inputs.map((input, index) => (
          <div
            key={`input-${index}-${input.name}`}
            className="flex items-center gap-2 rounded bg-zinc-900 px-2 py-1.5 border border-zinc-700"
          >
            <span className={`text-xs font-mono ${SOCKET_TYPE_COLORS[input.type]}`}>
              {input.type}
            </span>
            <span className="flex-1 text-xs text-zinc-300 truncate">{input.name}</span>
            {input.connected ? (
              <span className="text-xs text-green-500">connected</span>
            ) : (
              <>
                <input
                  type="text"
                  className="w-16 rounded bg-zinc-800 border border-zinc-600 px-1 py-0.5 text-xs text-zinc-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={input.defaultValue ?? ""}
                  onChange={(e) => {
                    const updatedInputs = [...properties.inputs];
                    updatedInputs[index] = { ...input, defaultValue: e.target.value };
                    onChange({ inputs: updatedInputs });
                  }}
                  placeholder="default"
                />
                <button
                  type="button"
                  className="flex h-5 w-5 items-center justify-center rounded bg-red-800 text-xs text-red-200 hover:bg-red-700 transition-colors"
                  title="Remove socket"
                  onClick={() => dispatchRemoveSocket(groupId, `in_${index}`, true)}
                >
                  -
                </button>
              </>
            )}
          </div>
        ))}
        <button
          type="button"
          className="flex items-center justify-center gap-1 rounded border border-dashed border-zinc-600 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-400 hover:text-zinc-300 transition-colors"
          onClick={handleAddInput}
        >
          <span>+</span> Add Input
        </button>
      </Section>

      {/* Outputs section with dynamic add/remove */}
      <Section title={`Outputs (${properties.outputs.length})`}>
        {properties.outputs.map((output, index) => (
          <div
            key={`output-${index}-${output.name}`}
            className="flex items-center gap-2 rounded bg-zinc-900 px-2 py-1.5 border border-zinc-700"
          >
            <span className={`text-xs font-mono ${SOCKET_TYPE_COLORS[output.type]}`}>
              {output.type}
            </span>
            <span className="flex-1 text-xs text-zinc-300 truncate">{output.name}</span>
            <button
              type="button"
              className="flex h-5 w-5 items-center justify-center rounded bg-red-800 text-xs text-red-200 hover:bg-red-700 transition-colors"
              title="Remove socket"
              onClick={() => dispatchRemoveSocket(groupId, `out_${index}`, false)}
            >
              -
            </button>
          </div>
        ))}
        <button
          type="button"
          className="flex items-center justify-center gap-1 rounded border border-dashed border-zinc-600 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-400 hover:text-zinc-300 transition-colors"
          onClick={handleAddOutput}
        >
          <span>+</span> Add Output
        </button>
      </Section>

      {/* Settings section */}
      {properties.settings && Object.keys(properties.settings).length > 0 && (
        <Section title="Settings">
          {Object.entries(properties.settings).map(([key, value]) => (
            <PropertyField
              key={key}
              label={key}
              type={
                typeof value === "boolean"
                  ? "checkbox"
                  : typeof value === "number"
                    ? "number"
                    : "text"
              }
              value={value}
              onChange={(v) => {
                onChange({
                  settings: { ...properties.settings, [key]: v },
                });
              }}
            />
          ))}
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section component
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">{title}</div>
      <div className="flex flex-col gap-2 pl-1">{children}</div>
    </div>
  );
}
